import { useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../services/api";
import SignupPage from "./SignupPage";

const decodeJwt = (token) => {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return {};
  }
};

const postAuth = async (path, body) => {
  const response = await fetch(`${BASE_URL}/api/v1/auth/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  let result = {};
  if (text) {
    try {
      result = JSON.parse(text);
    } catch {
      result = { message: text };
    }
  }
  return { ok: response.ok, result };
};

/** Email + password; when the distributor has login OTP on, then the verification code sent by SMS to the user's phone. */
const LoginPage = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [showSignup, setShowSignup] = useState(false);

  const completeLogin = (tokens) => {
    const claims = decodeJwt(tokens?.accessToken || "");
    onLoginSuccess({
      email,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      userId: claims.userId,
      distributorId: claims.distributorId,
      role: claims.role,
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { ok, result } = await postAuth("login", { email, password });
      if (!ok) {
        toast.error(result?.detail || result?.message || "Invalid email or password");
        return;
      }
      if (!result.otpRequired) {
        completeLogin(result.tokens);
        return;
      }
      setChallenge(result);
      setOtp("");
      toast.success(result.message || "Verification code sent");
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { ok, result } = await postAuth("login/verify", { challengeId: challenge.challengeId, otp: otp.trim() });
      if (!ok) {
        toast.error(result?.detail || result?.message || "Incorrect verification code");
        return;
      }
      completeLogin(result);
    } catch (error) {
      console.error("OTP verify error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const { ok, result } = await postAuth("login/resend", { challengeId: challenge.challengeId });
      if (!ok) {
        toast.error(result?.detail || result?.message || "Could not resend the code");
        return;
      }
      setChallenge(result);
      setOtp("");
      toast.success(result.message || "Verification code sent");
    } catch (error) {
      console.error("OTP resend error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const backToLogin = () => {
    setChallenge(null);
    setOtp("");
    setPassword("");
  };

  if (showSignup) {
    return (
      <SignupPage
        onBack={() => setShowSignup(false)}
        onSignedUp={(signedUpEmail) => {
          // back to the login form with the new account's email filled in
          setEmail(signedUpEmail || "");
          setPassword("");
          setShowSignup(false);
        }}
      />
    );
  }

  if (challenge) {
    return (
      <div className="login-container">
        <form className="login-form" style={{ width: "340px" }} onSubmit={handleVerify}>
          <h2>Enter Login OTP</h2>
          <p style={{ fontSize: "0.9rem", color: "#555", marginBottom: "12px" }}>
            {challenge.message || `Verification code sent to ${challenge.otpSentTo}`}
          </p>

          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="OTP"
            maxLength={10}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            autoFocus
            required
          />

          <button
            type="submit"
            className="primary"
            disabled={loading || !otp}
            style={{ width: "100%", marginTop: "8px", ...(loading ? { opacity: 0.6 } : {}) }}
          >
            {loading ? "Verifying..." : "Verify"}
          </button>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px", fontSize: "0.85rem" }}>
            <span onClick={loading ? undefined : backToLogin} style={{ cursor: "pointer", color: "#007bff" }}>
              Back to login
            </span>
            <span onClick={loading ? undefined : handleResend} style={{ cursor: "pointer", color: "#007bff" }}>
              Resend OTP
            </span>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="login-container">
      <form className="login-form" style={{ width: "340px" }} onSubmit={handleLogin}>
        <h2>Turbo Trade Admin</h2>

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <div style={{ position: "relative" }}>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <span
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              fontSize: "0.85rem",
              color: "#007bff",
            }}
          >
            {showPassword ? "Hide" : "Show"}
          </span>
        </div>

        <button
          type="submit"
          className="primary"
          disabled={loading}
          style={{ width: "100%", marginTop: "8px", ...(loading ? { opacity: 0.6 } : {}) }}
        >
          {loading ? "Logging in..." : "Login"}
        </button>
        <div style={{ marginTop: "12px", fontSize: "0.85rem", textAlign: "center" }}>
          <span onClick={loading ? undefined : () => setShowSignup(true)} style={{ cursor: "pointer", color: "#007bff" }}>
            New distributor? Sign up
          </span>
        </div>
      </form>
    </div>
  );
};

export default LoginPage;
